<template>
  <div>
    <NavBar />
    <LoadingWrapper :loading-bool="loading">
      <div v-if="site !== null" class="container">
        <div class="row mt-5">
          <div class="col-12">
            <h3>{{ site.name }}</h3>
          </div>
          <div class="col-12">
            {{ site.url }}
          </div>
        </div>

        <ProgressBar
          :evaluation-data="evaluation_data"
          :response-format="response_format"
          :jump-to-stage="jumpToStage" />

        <div class="mt-4">
          <Question
            v-if="currentQuestion"
            :parent-loading="subLoading"
            :question="currentQuestion"
            :delete-image-note="deleteImageNote"
            :save-image-note="saveImageNote"
            :image-notes="imageNotes[currentQuestion.id]"
            :next="nextQuestion"
            :prev="prevQuestion"
            :loading-errors="errors"
            :question-count="questionCount"
            :question-index="questionIndex" />
        </div>

        <Submission v-if="currentQuestion === null" :submit="complete" :loading="subLoading"
                    :complete-error="completeError" :jump-to-stage="jumpToStage" />
      </div>
    </LoadingWrapper>
    <Footer />
  </div>
</template>

<script>
  import apiMixin from "../../utils/api"
  import LoadingWrapper from "../navigation/LoadingWrapper"
  import Question from './subcomponents/Question'
  import Submission from './subcomponents/Submission'
  import ProgressBar from './subcomponents/ProgressBar'
  import Footer from "../navigation/Footer"
  import NavBar from "../navigation/Navbar"
  import tooltips from "../../resources/tooltips.json"

  export default {
    name: 'EvaluationCreate',
    components: {Footer, LoadingWrapper, NavBar, Question, Submission, ProgressBar},
    mixins: [apiMixin],
    props: {siteId: {type: Number, required: true}},
    data() {
      return {
        evaluationId: null,
        evaluation_data: null,
        response_format: null,
        site: null,
        imageNotes: {},

        completeError: null,

        submitting: false,
        currentQuestion: null,
        subLoading: false,
        questionCount: null
      }
    },
    computed: {
      questionIndex() {
        return !this.currentQuestion ? null : (this.response_format
          .reduce((acc, stage) => {
            return [...acc, ...this.getIntList(Object.keys(stage.data))]
          }, [])
          .sort((x, y) => x - y)
          .indexOf(this.currentQuestion.id)) + 1
      },
    },
    methods: {
      jumpToStage(stageId) {
        this.setCurrentQuestion(stageId, this.getFirstQuestionInStage(stageId))
      },
      prevQuestion() {
        const firstQuestionInStage = this.getFirstQuestionInStage(this.currentQuestion.stage.id)
        if (firstQuestionInStage === this.currentQuestion.id) {
          if (this.currentQuestion.stage.id !== 0) {
            const questionId = this.getLastQuestionInStage(this.currentQuestion.stage.id - 1)
            this.setCurrentQuestion(this.currentQuestion.stage.id - 1, questionId)
          }
        } else {
          this.setCurrentQuestion(this.currentQuestion.stage.id, this.currentQuestion.id - 1)
        }
      },
      async nextQuestion(question) {
        if (question.val !== undefined) {
          await this.saveCurrentAnswer(question)
        }

        const isLastStage = this.currentQuestion.stage.id === this.response_format.length - 1
        const isLastQuestionInStage = (
          this.getLastQuestionInStage(this.currentQuestion.stage.id) === this.currentQuestion.id
        )
        if (isLastQuestionInStage) {
          if (isLastStage) {
            this.currentQuestion = null
          } else {
            const nextQuestionId = this.getFirstQuestionInStage(this.currentQuestion.stage.id + 1)
            this.setCurrentQuestion(this.currentQuestion.stage.id + 1, nextQuestionId)
          }
        } else {
          this.setCurrentQuestion(this.currentQuestion.stage.id, this.currentQuestion.id + 1)
        }
      },
      setCurrentQuestion(stageId, questionId) {
        this.currentQuestion = {
          id: questionId,
          title: this.response_format[stageId].data[questionId],
          stage: {
            id: stageId,
            title: this.response_format[stageId].name
          },
          val: this.evaluation_data[questionId],
          tooltip: tooltips[questionId],
          note: this.notes[questionId],
          evaluationId: this.evaluationId,
          siteId: this.siteId,
        }
      },
      async complete() {
        this.completeError = null
        return this.withLoading(async () => {
          try {
            await this.post(
              `/api/sites/${this.siteId}/evaluations/${this.evaluationId}/completion/`
            )
          } catch (e) {
            this.completeError = e.error
            throw e
          }
          this.$router.push({
            name: 'evaluation.detail',
            params: {siteId: this.siteId, evaluationId: this.evaluationId}
          })
        }, 'subLoading')
      },
      async saveCurrentAnswer(question) {
        this.errors = []
        return this.withLoading(async () => {
          try {
            await this.put(
              `/api/sites/${this.siteId}/evaluations/${this.evaluationId}/questions/${this.currentQuestion.id}/answer/`,
              {
                value: question.val,
                note: question.note
              }
            )
            this.$set(this.evaluation_data, this.currentQuestion.id, question.val)
            this.$set(this.notes, this.currentQuestion.id, question.note)
          } catch (e) {
            this.catchApiError(e)
          }
        }, 'subLoading')
      },

      // Initial load call.
      load() {
        this.withLoading(async () => {
          try {
            let evaluation = (await this.get(
              `/api/sites/${this.siteId}/evaluations/current/`
            )).items[0]

            this.evaluationId = evaluation.id

            this.getImageNotes()
            this.response_format = evaluation.response_format
            this.evaluation_data = evaluation.evaluation_data || {}
            this.notes = evaluation.notes.reduce((accumulator, currVal) => {
              accumulator[currVal.question_id] = currVal.content
              return accumulator
            }, {})
            this.site = evaluation.site
            this.questionCount = this.response_format.reduce(
              (acc, section) => acc + Object.keys(section.data).length,
              0
            )

            const questionId = Math.max(
              ...this.getIntList(Object.keys(this.evaluation_data)),
              this.getStageKeys(0)[0]
            )
            const questionStage = this.response_format
              .findIndex((_, stageId) => this.getStageKeys(stageId).includes(questionId))
            this.setCurrentQuestion(questionStage, questionId)
          } catch (e) {
            this.error = e.error
            if (e.status === 404) {
              this.$router.push({name: 'site.list'})
            }
          }
        })
      },
      getIntList(strList) {
        return strList.map(x => parseInt(x, 10))
      },
      getStageKeys(stageId) {
        return this.getIntList(Object.keys(this.response_format[stageId].data))
      },
      getFirstQuestionInStage(stageId) {
        return Math.min(...this.getStageKeys(stageId))
      },
      getLastQuestionInStage(stageId) {
        return Math.max(...this.getStageKeys(stageId))
      },
      async getImageNotes() {
        const imageNotes = await this.get(
          `/api/sites/${this.siteId}/evaluations/${this.evaluationId}/image-notes/`
        )
        this.imageNotes = imageNotes.items.reduce((accumulator, item) => {
          if (accumulator[item.question_id]) {
            accumulator[item.question_id].push(item)
          } else {
            accumulator[item.question_id] = [item]
          }
          return accumulator
        }, {})
      },
      async saveImageNote(body) {
        const newImage = await this.postForm(
          `/api/sites/${this.currentQuestion.siteId}/evaluations/${this.currentQuestion.evaluationId}/questions/${this.currentQuestion.id}/image-note/`,
          body
        )
        if (typeof this.imageNotes[newImage.question_id] === 'undefined') {
          this.imageNotes[newImage.question_id] = [newImage]
        } else {
          this.imageNotes[newImage.question_id].unshift(newImage)
        }
        this.$forceUpdate()
      },
      async deleteImageNote(item) {
        await this.delete(`/api/sites/${this.siteId}/evaluations/${this.evaluationId}/image-note/${item.id}/`, {})
        this.imageNotes[item.question_id] = this.imageNotes[item.question_id].filter(img => img.id !== item.id)
        this.$forceUpdate()
      },
    }
  }

</script>

<style scoped lang="scss">
  h1, h4 {
    display: block;
  }

  .heading {
    @media (max-width: 767px) {
      text-align: center;
    }
  }
</style>